
























































































import { Component, Vue } from 'vue-property-decorator'
import CalendarAction from '@/views/Actions/CalendarAction.vue'
import { ActionAPI } from '@/api/backend-client'
import { Action } from '@/api/model/action'
import { ActionType } from '@/api/types'
import { User } from '@/api/model/user'
import { isAllowedUserForReferral } from '@/helpers/security'
import moment from 'moment'

@Component({
    components: {
        CalendarAction,
    },
})

export default class Calendar extends Vue {
    loading: boolean = false
    actions: Action[] = []
    view: string = 'user'
    startDate: Date = null
    endDate: Date = null
    showTasks: boolean = true
    showMeetings: boolean = true

    mounted() {
        if ('view' in this.$route.query) {
            this.changeView(this.$route.query.view as string)
        }

        if ('date' in this.$route.query) {
            this.setDate(new Date(this.$route.query.date as string))
        } else {
            this.setToday()
        }
    }

    get availableViews(): string[] {
        return this.$currentUser.isTeamLead ? ['user', 'branches'] : ['user']
    }

    get availableViewsOptions() {
        return this.availableViews
            .map(availableView => ({
                label: this.$t(`actions-calendar.view.${availableView}`),
                value: availableView,
            }))
    }

    changeView(view: string): void {
        if (!this.availableViews.includes(view)) {
            return
        }

        this.view = view
    }

    async setToday(): Promise<void> {
        await this.setDate(new Date())
    }

    async setDate(date: Date): Promise<void> {
        this.startDate = moment(date).startOf('week').toDate()
        this.endDate = moment(this.startDate).add(6, 'days').endOf('day').toDate()
        await this.loadActions()
        this.scrollToOfficeHours()
    }

    async prevDate(): Promise<void> {
        this.startDate = moment(this.startDate).subtract(1, 'week').startOf('day').toDate()
        this.endDate = moment(this.startDate).add(6, 'days').endOf('day').toDate()
        await this.loadActions()
        this.scrollToOfficeHours()
    }

    async nextDate(): Promise<void> {
        this.startDate = moment(this.startDate).add(1, 'week').startOf('day').toDate()
        this.endDate = moment(this.startDate).add(6, 'days').endOf('day').toDate()
        await this.loadActions()
        this.scrollToOfficeHours()
    }

    async loadActions(): Promise<void> {
        this.loading = true
        this.actions = await ActionAPI.getAll({
            'completed': false,
            'view': 'branches',
            'actionDate[after]': this.startDate,
            'actionDate[before]': this.endDate,
        })
        this.loading = false
    }

    get filteredActions(): Action[] {
        return this.actions
            .filter((action: Action) => {
                if (action.type !== ActionType.MEETING && !this.showTasks) {
                    return false
                } else if (action.type === ActionType.MEETING && !this.showMeetings) {
                    return false
                }

                if (!moment(action.actionDate).isSameOrAfter(this.startDate, 'day')) {
                    return false
                }

                if (!moment(action.actionDate).isSameOrBefore(this.endDate, 'day')) {
                    return false
                }

                if (this.view === 'user') {
                    return action.assignedTo.some((user: User) => {
                        return user.id === this.$currentUser.id
                    })
                }

                return true
            })
            .sort((a: Action, b: Action) => {
                return moment(a.actionDate).unix() - moment(b.actionDate).unix()
            })
    }

    get filteredActionsByDateAndHour() {
        const actionsByDate = {}

        this.filteredActions
            .forEach((action: Action) => {
                const actionDate = moment(action.actionDate).format('YYYY-MM-DD')
                if (!(actionDate in actionsByDate)) {
                    actionsByDate[actionDate] = {}
                }

                const actionHour = moment(action.actionDate).format('H')
                if (!(actionHour in actionsByDate[actionDate])) {
                    actionsByDate[actionDate][actionHour] = []
                }

                actionsByDate[actionDate][actionHour].push(action)
            })

        return actionsByDate
    }

    get week() {
        return moment(this.startDate).format('W')
    }

    get dates() {
        const dates = []

        let date = moment(this.startDate)
        while (date.isSameOrBefore(this.endDate, 'day')) {
            dates.push(date.format('YYYY-MM-DD'))
            date = moment(date).add('1', 'day')
        }

        return dates
    }

    get hours() {
        const hours = []

        for (let hour = 0; hour <= 23; hour++) {
            hours.push(hour)
        }

        return hours
    }

    actionsForDateAndHour(date: string, hour: string): Action[] {
        if (!(date in this.filteredActionsByDateAndHour)) {
            return []
        }

        if (!(hour in this.filteredActionsByDateAndHour[date])) {
            return []
        }

        return this.filteredActionsByDateAndHour[date][hour]
    }

    classesForDateAndHour(date: string, hour: string) {
        return {
            'bg-gray-100': !this.isOfficeHours(date, hour),
        }
    }

    isOfficeHours(date: string, hour: string) {
        const hourAsNumber = Number.parseInt(hour, 10)
        if (hourAsNumber < 9 || hourAsNumber >= 18) {
            return false
        }

        return true
    }

    async scrollToOfficeHours(): Promise<void> {
        await this.$nextTick(() => {
            const calendarEl = document.querySelector('.actions-calendar') as HTMLElement
            const calendarHeaderEl = calendarEl.querySelector(':first-child') as HTMLElement
            const firstOfficeHoursElement = calendarEl.querySelector('[data-is-office-hours]') as HTMLElement

            firstOfficeHoursElement.parentElement.scrollTop = firstOfficeHoursElement.offsetTop - 1.5 * calendarHeaderEl.clientHeight
        })
    }

    goToDetail(action: Action) {
        if (!isAllowedUserForReferral(this.$currentUser, action.referral) && this.$currentUser.betaOptInEnabled) {
            return this.$router.push({
                name: 'one_contact_detail',
                params: {
                    contactId: action.referral.contact.id,
                    toOne: this.$currentUser.betaOptInEnabled as unknown as string,
                },
            })
        }

        return this.$router.push({
            name: 'tasks_detail',
            params: {
                referralId: action.referral.id,
                contactId: action.referral.contact.id,
                toOne: this.$currentUser.betaOptInEnabled as unknown as string,
            },
        })
    }
}
